// Ported from app/assets/javascripts/application/project_browser.js.coffee
//
// Breadcrumb overflow handling, the Browse/Recent view switch, and the
// project search box (debounced fetch, keyboard nav, help chips). All
// handlers are delegated through document so they survive Turbo
// navigations.

function isVisible(el: HTMLElement | null): boolean {
  if (!el) return false
  // Mirrors jQuery's :visible — element occupies layout space.
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
}

function searchResults(): HTMLElement | null {
  return document.getElementById("search-results")
}

function searchHelp(): HTMLElement | null {
  return document.getElementById("search-help")
}

function searchInput(): HTMLInputElement | null {
  return document.getElementById("search-input") as HTMLInputElement | null
}

function hideResults(): void {
  const r = searchResults()
  if (r) r.style.display = "none"
}

// Hide both dropdowns (live results + help reference).
function hideAll(): void {
  hideResults()
  searchHelp()?.classList.add("hidden")
}

// True if either dropdown is currently open.
function dropdownOpen(): boolean {
  return isVisible(searchResults()) || isVisible(searchHelp())
}

// Shows the pre-rendered help reference instantly when the search term is
// exactly "help:" — no server round-trip. Returns true if help is showing.
// When it's not the help term, the panel is hidden (results path proceeds).
function maybeShowHelp(term: string): boolean {
  const help = searchHelp()
  if (!help) return false
  const isHelp = term.trim().toLowerCase() === "help:"
  help.classList.toggle("hidden", !isHelp)
  if (isHelp) hideResults() // never show both at once
  return isHelp
}

function fitBreadcrumb(): void {
  document.querySelectorAll<HTMLElement>(".work-panel-trail").forEach((trail) => {
    if (trail.scrollWidth > trail.clientWidth) {
      trail.classList.add("is-overflowing")
      trail.scrollLeft = trail.scrollWidth
    } else {
      trail.classList.remove("is-overflowing")
    }
  })
}

function switchView(view: string): void {
  document.querySelectorAll<HTMLElement>(".work-panel").forEach(el => {
    el.setAttribute("data-view", view)
  })
  document.querySelectorAll<HTMLElement>(".view-switch-btn").forEach(btn => {
    btn.classList.toggle("is-active", btn.dataset.view === view)
  })
}

// ----- Search results keyboard selection -----

function checkSelected(): boolean {
  if (document.querySelector(".selected-search-item")) return false
  searchResults()?.querySelector("li")?.classList.add("selected-search-item")
  return true
}

function upArrow(): void {
  if (checkSelected()) return
  const item = document.querySelector<HTMLElement>(".selected-search-item")
  const prev = item?.previousElementSibling as HTMLElement | null
  if (item && prev) {
    item.classList.remove("selected-search-item")
    prev.classList.add("selected-search-item")
  }
}

function downArrow(): void {
  if (checkSelected()) return
  const item = document.querySelector<HTMLElement>(".selected-search-item")
  const next = item?.nextElementSibling as HTMLElement | null
  if (item && next) {
    item.classList.remove("selected-search-item")
    next.classList.add("selected-search-item")
  }
}

async function getSearchResults(): Promise<void> {
  const input = searchInput()
  const results = searchResults()
  if (!input || !results) return
  const term = input.value
  if (term.trim() === "") {
    hideAll()
    return
  }
  // "help:" shows the pre-rendered reference — no fetch.
  if (maybeShowHelp(term)) return
  const ctx = /context=([cpt]\d+)/.exec(location.search)
  const params = new URLSearchParams({ term })
  if (ctx && ctx[1]) params.set("context", ctx[1])
  try {
    const res = await fetch(`/project-search?${params}`, {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    })
    results.innerHTML = await res.text()
    // Explicit "block", not "" — the stylesheet defaults #search-results to
    // display:none, so clearing the inline style would just re-hide it.
    results.style.display = "block"
  } catch (_e) {
    /* leave results as-is on error */
  }
}

// ===== Module-scope: bind once, survive Turbo navigations =====

window.addEventListener("resize", fitBreadcrumb)

document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement

  // View switcher
  const viewBtn = target.closest<HTMLElement>(".view-switch-btn")
  if (viewBtn) {
    e.preventDefault()
    switchView(viewBtn.dataset.view ?? "")
    return
  }

  // Help prefix chip / empty-state link → drop the prefix into the search box.
  const helpChip = target.closest<HTMLElement>(
    ".search-help-prefixes dt code, .search-empty-help-link"
  )
  if (helpChip) {
    e.preventDefault()
    const text = helpChip.classList.contains("search-empty-help-link")
      ? "help:"
      : (helpChip.textContent ?? "").trim()
    if (text.indexOf("[") >= 0) return // template placeholder, not a real prefix
    const input = searchInput()
    if (input) {
      input.value = `${text} `
      input.focus()
      maybeShowHelp(input.value) || getSearchResults()
    }
    return
  }

  // Search clear button
  if (target.closest("#search-clear")) {
    const input = searchInput()
    if (input) input.value = ""
    hideAll()
    return
  }

  // Help affordance next to the search box → show help instantly.
  if (target.closest(".work-panel-search-help")) {
    const input = searchInput()
    if (input) {
      input.value = "help:"
      input.focus()
      maybeShowHelp("help:")
    }
  }
})

// Instant help toggle — fires as the user types, independent of the
// debounced search below, so "help:" surfaces the reference immediately.
document.addEventListener("input", (e) => {
  if ((e.target as HTMLElement).id !== "search-input") return
  if (maybeShowHelp((e.target as HTMLInputElement).value) && searchTimeoutId) {
    clearTimeout(searchTimeoutId)
  }
})

// Click outside the search area closes the dropdown.
document.addEventListener("mousedown", (e) => {
  if (!dropdownOpen()) return
  const target = e.target as HTMLElement
  if (!target.closest(".work-panel-search, #search-results, #search-help")) hideAll()
})

document.addEventListener("keydown", (e) => {
  // Esc closes the open dropdown.
  if (e.key === "Escape" && dropdownOpen()) {
    hideAll()
    return
  }

  // Search input keyboard handling.
  const target = e.target as HTMLElement
  if (target.id !== "search-input") return

  if (searchTimeoutId) clearTimeout(searchTimeoutId)
  if (e.key === "Enter") {
    const selected = document.querySelector<HTMLElement>(".selected-search-item")
    const href = selected?.querySelector("a")?.getAttribute("href")
    if (href) window.location.href = href
    else getSearchResults()
  } else if (e.key === "Escape") {
    hideAll()
    ;(target as HTMLInputElement).select()
  } else if (e.key === "ArrowUp") {
    e.preventDefault()
    upArrow()
  } else if (e.key === "ArrowDown") {
    e.preventDefault()
    downArrow()
  } else {
    searchTimeoutId = window.setTimeout(getSearchResults, 500)
    document
      .querySelector(".selected-search-item")
      ?.classList.remove("selected-search-item")
  }
})

let searchTimeoutId: number | undefined

// ===== Per-page setup on every Turbo navigation =====
document.addEventListener("turbo:load", () => {
  fitBreadcrumb()
  // Smooth-scroll to the current context task, if the page has one.
  document.querySelector(".context-task")?.scrollIntoView({ behavior: "smooth", block: "center" })
})
